import React from 'react';

/**
 * Druckbare Rechnungsvorschau im Medifox-Stil (PDF-konform)
 * Layout entspricht 1:1 dem Typst-generierten PDF
 */
const PrintableInvoice = ({ ergebnis, rechnungsNr, variant = "ba", pflegedienst = "kreuzberg", pflegedienstData = {} }) => {
  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };
  const formatNumber = (value) => {
    const num = parseFloat(value) || 0;
    return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const isBA = variant === "ba";
  const data = isBA ? ergebnis?.baRechnung : ergebnis?.privatRechnung;
  const zusammenfassung = ergebnis?.zusammenfassung || {};
  const originalRechnung = ergebnis?.originalRechnung || {};

  // Pflegedienst-Stammdaten (identisch mit Backend PFLEGEDIENSTE)
  const pflegedienstInfo = {
    kreuzberg: {
      name: "DomusVita Gesundheit GmbH",
      strasse: "Waldemarstraße 10a",
      plz: "10999",
      ort: "Berlin",
      ik: "461104151",
      telefon: "030 53 00 555 0",
      telefax: "030 53 00 555 530",
      email: "info@domusvita.de",
      web: "www.domusvita.de",
      bank: "Berliner Sparkasse",
      blz: "10050000",
      kontoNr: "0190998920",
      iban: "DE19 1005 0000 0190 9989 20",
      bic: "BELADEBEXXX",
      handelsregister: "AG Berlin Charlottenburg HRB 127949B",
      geschaeftsfuehrer: "Lukas Dahrendorf, Alexander Ebel, Daniel Ebel"
    },
    treptow: {
      name: "DomusVita Gesundheit GmbH",
      strasse: "Baumschulenstr. 24",
      plz: "12437",
      ort: "Berlin",
      ik: "461104152",
      ikAlt: "461109934",
      telefon: "030 53 00 555 0",
      telefax: "030 53 00 555 530",
      email: "info@domusvita.de",
      web: "www.domusvita.de",
      bank: "Berliner Sparkasse",
      blz: "10050000",
      kontoNr: "0190998920",
      iban: "DE19 1005 0000 0190 9989 20",
      bic: "BELADEBEXXX",
      handelsregister: "AG Berlin Charlottenburg HRB 127949B",
      geschaeftsfuehrer: "Lukas Dahrendorf, Alexander Ebel, Daniel Ebel"
    }
  };

  const pd = pflegedienstData[pflegedienst] || pflegedienstInfo[pflegedienst] || pflegedienstInfo.kreuzberg;
  const standortName = pflegedienst === 'treptow' ? 'Treptow' : 'Kreuzberg';

  // Positionen aufbereiten
  const positionen = data?.positionen || [];

  // Empfänger
  const rechnungsEmpfaenger = zusammenfassung.rechnungsEmpfaenger;
  const empfaenger = isBA ? {
    name: rechnungsEmpfaenger?.name || "[Bezirksamt]",
    ansprechpartner: rechnungsEmpfaenger?.ansprechpartner || "",
    strasse: rechnungsEmpfaenger?.strasse || "",
    plzOrt: rechnungsEmpfaenger?.plzOrt || ""
  } : {
    name: zusammenfassung.klient || "[Leistungsempfänger]",
    ansprechpartner: "",
    strasse: zusammenfassung.klientAdresse?.split(',')[0] || "",
    plzOrt: zusammenfassung.klientAdresse?.split(',')[1]?.trim() || ""
  };

  // Datum und Zahlungsziel
  const today = new Date();
  const paymentDate = new Date(today);
  paymentDate.setDate(paymentDate.getDate() + 14);
  const formatDateDE = (d) => d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // Zeitraum aufteilen
  const zeitraum = zusammenfassung.zeitraum || zusammenfassung.abrechnungszeitraum || "";
  const zeitraumParts = zeitraum.replace(" ", "").split("-");
  const zeitraumVon = zeitraumParts[0]?.trim() || "";
  const zeitraumBis = zeitraumParts[1]?.trim() || "";

  // Klient-Daten
  const klientGeburtsdatum = originalRechnung.klientGeburtsdatum || zusammenfassung.klientGeburtsdatum || "";
  const klientVersichertenNr = originalRechnung.klientVersichertenNr || zusammenfassung.klientVersichertenNr || "";

  // Summen berechnen (wie im PDF)
  const gesamtbetrag = (data?.zwischensumme || 0) + (data?.zinv || 0);

  return (
    <div className="p-6 bg-white text-[9pt] font-sans leading-tight" style={{ fontFamily: 'Arial, sans-serif' }}>
      
      {/* === BRIEFKOPF === */}
      <div className="flex justify-between mb-4">
        {/* Logo links */}
        <div>
          <img src={pflegedienst === 'treptow' ? '/logo_treptow.png' : '/logo_kreuzberg.png'} 
               alt="DomusVita" className="h-16" 
               onError={(e) => { e.target.src = '/logo.png'; }} />
        </div>
        
        {/* Firmendaten rechts */}
        <div className="text-right text-[9pt]">
          <div className="font-bold">{pd.name}</div>
          <div>{pd.strasse}</div>
          <div>{pd.plz} {pd.ort}</div>
          <div className="mt-2">
            <div>Telefon: {pd.telefon}</div>
            <div>Telefax: {pd.telefax}</div>
            <div>E-Mail: {pd.email}</div>
            <div>{pd.web}</div>
          </div>
        </div>
      </div>

      {/* === ABSENDERZEILE + HANDELSREGISTER === */}
      <div className="flex justify-between text-[7pt] text-gray-600 mb-4">
        <div className="border-b border-black pb-0.5">
          {pd.name} - {standortName} - {pd.strasse} - {pd.plz} {pd.ort}
        </div>
        <div className="text-right">
          <div>{pd.handelsregister}</div>
          <div>Geschäftsführer:</div>
          <div>{pd.geschaeftsfuehrer}</div>
        </div>
      </div>

      {/* === EMPFÄNGER === */}
      <div className="mb-6">
        <div className="font-bold">{empfaenger.name}</div>
        {empfaenger.ansprechpartner && <div>{empfaenger.ansprechpartner}</div>}
        <div>{empfaenger.strasse}</div>
        <div>{empfaenger.plzOrt}</div>
      </div>

      {/* === RECHNUNGSKOPF === */}
      <div className="flex justify-between mb-4">
        <div>
          <div className="font-bold text-[11pt]">Rechnung Nr.: {rechnungsNr}</div>
          <div className="text-[9pt]">Abrechnungszeitraum von: {zeitraumVon} bis: {zeitraumBis} nach § 36 SGB XI</div>
        </div>
        <div className="text-right">
          <div><strong>Datum:</strong> {formatDateDE(today)}</div>
          <div><strong>IK:</strong> {pd.ik}</div>
        </div>
      </div>

      {/* === LEISTUNGSEMPFÄNGER-BOX (2-Spalten) === */}
      <div className="border border-gray-400 p-3 mb-4 bg-gray-50">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-[8pt] font-bold text-gray-600">Leistungsempfänger:</div>
            <div>{zusammenfassung.klient}</div>
            <div>{zusammenfassung.klientAdresse}</div>
          </div>
          <div className="text-[8pt]">
            <div className="grid grid-cols-2 gap-1">
              <div>Geburtsdatum:</div>
              <div>{klientGeburtsdatum}</div>
              <div>Pflegegrad:</div>
              <div>{zusammenfassung.pflegegrad}</div>
              <div>Versicherten-Nr.:</div>
              <div>{klientVersichertenNr}</div>
            </div>
          </div>
        </div>
      </div>

      {/* === TABELLE === */}
      <table className="w-full border-collapse text-[8pt] mb-4">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-1 text-left w-[10%]">Abkürzung</th>
            <th className="border border-gray-300 p-1 text-left w-[38%]">Leistung</th>
            <th className="border border-gray-300 p-1 text-right w-[10%]">Anzahl</th>
            <th className="border border-gray-300 p-1 text-right w-[10%]">Abr. Anz.</th>
            <th className="border border-gray-300 p-1 text-right w-[14%]">Einzelpreis €</th>
            <th className="border border-gray-300 p-1 text-right w-[18%]">Gesamtpreis €</th>
          </tr>
        </thead>
        <tbody>
          {positionen.map((pos, idx) => {
            const code = pos.istAUB ? 'AUB' : (pos.lkCode || '-');
            const bezeichnung = pos.bezeichnung || '';
            const menge = pos.menge || 0;
            const abrMenge = pos.abrMenge || menge;
            const preis = pos.preis || 0;
            const gesamt = pos.gesamt || 0;
            
            return (
              <tr key={idx} className={gesamt === 0 ? 'text-gray-400' : ''}>
                <td className="border border-gray-300 p-1">{code}</td>
                <td className="border border-gray-300 p-1">
                  {bezeichnung}
                  {pos.umgewandeltZu && (
                    <div className="text-[7pt] text-gray-500">→ {pos.umgewandeltZu}</div>
                  )}
                  {pos.gekuerztVon && (
                    <div className="text-[7pt] text-gray-500">↓ {pos.gekuerztVon} → {menge}</div>
                  )}
                </td>
                <td className="border border-gray-300 p-1 text-right font-mono">{formatNumber(menge)}</td>
                <td className="border border-gray-300 p-1 text-right font-mono">{formatNumber(abrMenge)}</td>
                <td className="border border-gray-300 p-1 text-right font-mono">{formatNumber(preis)}</td>
                <td className="border border-gray-300 p-1 text-right font-mono font-bold">{formatCurrency(gesamt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* === HINWEIS (falls Positionen mit 0,00 €) === */}
      {positionen.some(p => p.gesamt === 0 && p.menge > 0) && (
        <div className="bg-yellow-50 border border-yellow-300 p-2 mb-4 text-[8pt]">
          <strong>Hinweis:</strong> Positionen mit 0,00 € wurden dokumentarisch aufgeführt (Umwandlung/Kürzung), fließen jedoch nicht in die Rechnungssumme ein.
        </div>
      )}

      {/* === SUMMENBEREICH === */}
      <div className="flex justify-end mb-4">
        <table className="text-[9pt]">
          <tbody>
            <tr>
              <td className="pr-4">Gesamtbetrag:</td>
              <td className="text-right font-mono">{formatCurrency(gesamtbetrag)}</td>
            </tr>
            {isBA && data?.pflegekasse > 0 && (
              <tr>
                <td className="pr-4">./. Anteil weitere Kostenträger:</td>
                <td className="text-right font-mono">{formatCurrency(data.pflegekasse)}</td>
              </tr>
            )}
            <tr className="border-t-2 border-black font-bold">
              <td className="pr-4 pt-1">Rechnungsbetrag:</td>
              <td className="text-right font-mono pt-1">{formatCurrency(data?.rechnungsbetrag)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* === ZAHLUNGSHINWEIS === */}
      <div className="text-[8pt] mb-4">
        Bitte überweisen Sie den Rechnungsbetrag bis zum {formatDateDE(paymentDate)} auf untengenanntes Konto.<br/>
        Die abgerechneten Leistungen sind gemäß § 4 Nr. 16 UStG umsatzsteuerbefreit.
      </div>

      {/* === FOOTER === */}
      <div className="border-t border-gray-300 pt-2 text-[8pt] text-gray-600">
        Bankverbindung: {pd.bank} · BLZ {pd.blz} · Konto-Nr. {pd.kontoNr}<br/>
        IBAN {pd.iban}
      </div>
    </div>
  );
};

export default React.memo(PrintableInvoice);
